/**
 * Static sidebar navigation for the whole backoffice shell. This is
 * Workmate-X-requirement-based information architecture (worker safety /
 * worker line responsibility / worker intake lifecycle / Thailand job
 * demand / platform governance) - not a generic admin-panel menu, and not
 * the rejected P082/P083 IA.
 *
 * "Dashboard", "Footstep", "Super Agents", "Invite / Applications",
 * "e-KYC Review", "Ready for Match", "Worker Profiles",
 * "Thailand Jobs > Employers" (which now also owns the Jobs and Worksites
 * screens as inner screens rather than sibling menu items),
 * "Thailand Jobs > Matching Queue", and
 * "Thailand Jobs > Assignments" are the only real, implemented screens so
 * far. Every other item is reserved: the screen genuinely does not exist
 * yet, so it renders disabled with an honest explanation instead of linking
 * to a page that isn't there. Real per-user scoping still happens entirely
 * on the backend behind whichever routes eventually exist.
 *
 * "บริษัทจัดหางาน" / "บริษัทจัดหาแรงงาน" (the two side-scoped views of
 * /backoffice/worker-line/plcs - see the WORKER LINE section below), "Agents"
 * (/backoffice/worker-line/agents), and "Worker Line Org Chart"
 * (/backoffice/worker-line/org-chart) (Prompt 132) are now also real,
 * implemented screens - the Worker Line Admin CRUD API (Prompt 131) built
 * the backend, this packet built the Create/Edit/list/detail UI over it and
 * flipped these three items from reservedNote to a real `to`. "Super
 * Agents" itself was already real (P085/P086R); this packet rebuilt its
 * list/detail to call the real Worker Line Admin API instead of deriving
 * everything from the read-only hierarchy tree, and added its own
 * Create/Edit.
 *
 * "Footstep" (Prompt 115B) links to /backoffice/tracking/footsteps - the
 * real Backoffice Footstep History screen (Prompt 114 API), built from the
 * W55 reference. Prompt 113B originally pointed this item at
 * /backoffice/tracking/latest (the Latest Tracking Status screen, Prompt
 * 112 API) as a placeholder, since no dedicated Footstep History screen
 * existed yet and canViewFootstepHistory was always rendered blocked there.
 * Now that Footstep History is implemented, this item points at its real
 * destination; /backoffice/tracking/latest stays mounted in App.jsx and
 * reachable by direct URL, matching the existing precedent of routes
 * reachable only outside the sidebar (e.g. Assignment Onboarding).
 *
 * "Missing Heartbeat" links to /backoffice/abnormal/heartbeat - the real
 * Backoffice Abnormal Heartbeat screen (Prompt 116 API), built from the W56
 * reference. It never implements geofence, fake-location scoring, or face
 * recognition (none of those exist anywhere in this codebase).
 *
 * "Geofence Zones" (Prompt 119B) links to /backoffice/geofence/zones - the
 * real Backoffice Geofence Zones screen (Prompt 118 API), built from the
 * W57 reference. The existing IA had no prior "Geofence" entry, so this is a
 * new sibling item added directly under COMMAND (next to War Room, SOS /
 * Abnormal, and Footstep - the same family of worker-safety/tracking
 * screens this page belongs to), matching W57's own IA decision exactly.
 * This is Circle zones only - it never implements polygon geometry,
 * fake-location scoring, face recognition, or SOS (none of those exist
 * anywhere in this codebase).
 *
 * "Fake Location Review" (Prompt 121B) links to
 * /backoffice/tracking/fake-location - the real Backoffice Fake Location
 * Review screen (Prompt 120 API), built from the W58 reference. The
 * existing IA had no prior entry for this screen, so this is a new sibling
 * item added directly under COMMAND, right after Geofence Zones - the same
 * family of worker-safety/tracking screens this page belongs to. Scoring
 * is fixed, rule-based, and explainable - it never implies AI/ML, never
 * calls an external anti-fraud/geolocation service, never evaluates a
 * geofence, and never implies automatic worker punishment/blocking (none
 * of those exist anywhere in this codebase).
 *
 * "Face Check" (Prompt 123B) has NO nav item, by the owner's own instruction
 * ("ตัดเมนู face check ออกครับ"). The SCREEN is untouched and its route,
 * /backoffice/face-check/daily, still resolves - the same rule this app
 * already follows for Worksites and Jobs: dropping a menu entry hides a door,
 * it does not demolish the room, so deep links, bookmarks and any link from
 * another screen keep working exactly as before.
 *
 * "SOS Cases" links to /backoffice/sos/cases - the real Backoffice SOS
 * Cases screen (Prompt 124 API), built from the W60 reference. This screen
 * never sends a real notification, never calls an external
 * emergency-service integration, and never runs a medical/legal advice
 * engine - sosType is a plain worker-picked category label, nothing more.
 *
 * P144-R splits the old combined "SOS / Abnormal" single nav item (Prompt
 * 125B, itself building on Prompt 117B) into these two separate,
 * independently-reachable items - "Missing Heartbeat" and "SOS Cases",
 * adjacent in COMMAND. The prior design deliberately kept one shared label
 * pointing only at the Abnormal Heartbeat route (SOS Cases reachable only
 * by direct URL/activeMatch highlighting) because at the time "SOS /
 * Abnormal" already named both concepts and splitting felt like an
 * unnecessary second item. This packet's own consistency-lock work made the
 * domain boundary between "SOS = emergency" and "Missing Heartbeat =
 * monitoring/tracking compliance" a hard requirement end-to-end (War Room's
 * red status now always names a real sourceType of "Sos" or
 * "AbnormalEvent") - a single ambiguous sidebar entry no longer matches that,
 * so both real screens now get their own, equally discoverable link.
 *
 * "War Room" (Prompt 135) now links to /backoffice/war-room - a real,
 * functional command board (W61 reference), not a reserved item. It calls
 * the exact same Prompt 112 Backoffice Latest Tracking read API the
 * existing Latest Tracking Status screen already calls
 * (GET .../tracking/workers/latest, GET .../tracking/workers/{id}/latest),
 * so this flip introduces no new backend endpoint. Scope
 * (SuperAdmin/SuperAgent/PLC/Agent) is resolved entirely server-side from
 * the caller's session token; the frontend never supplies or fakes a scope
 * id. It never renders a map, never implies real push/SMS/LINE notification
 * delivery, and never implies Wi-Fi/cellular fingerprint data (none of
 * those exist anywhere in this codebase).
 *
 * "Worker Messages" (P154) linked to /backoffice/messages - the real Agent
 * half of the worker-to-Agent conversation the mobile app writes into, over
 * the P154 Backoffice Chat API (GET .../chat/threads,
 * GET|POST .../chat/threads/{threadId}[/messages]). P161 removed it from THIS
 * (sysadmin) menu at the owner's instruction; the screen, the route and the
 * Agent Portal's own SUPPORT > Messages entry pointing at it are all
 * unchanged.
 */
export const NAV_SECTIONS = [
  {
    title: "COMMAND",
    items: [
      { key: "dashboard", mark: "D", label: "Dashboard", to: "/backoffice" },
      { key: "war-room", mark: "W", label: "War Room", to: "/backoffice/war-room" },
      { key: "missing-heartbeat", mark: "M", label: "Missing Heartbeat", to: "/backoffice/abnormal/heartbeat" },
      { key: "sos-cases", mark: "S", label: "SOS Cases", to: "/backoffice/sos/cases" },
      // P159: "Footstep" folded into War Room on 2026-08-08 at the owner's
      // instruction - a person's footstep is now read from the "ดู Footstep"
      // button at the top of the War Room person drawer, in the context of the
      // person you are investigating. The ROUTE stays registered in App.jsx, so
      // bookmarks, audit-log links and the drawer's own deep links all still
      // resolve; only the standalone menu entry is gone. Same fold-into-parent
      // move as Worksites/Jobs -> Employers.
      { key: "geofence-zones", mark: "G", label: "Geofence Zones", to: "/backoffice/geofence/zones" },
      // Fake Location Review hidden from the sidebar on 2026-08-03 at the
      // owner's request. The ROUTE is deliberately left registered in App.jsx:
      // an existing bookmark or an audit-log link to it keeps working, and
      // bringing the item back later is one line here. Only the nav entry is
      // gone, not the screen.
      // P161: "Worker Messages" removed from the SuperAdmin menu on 2026-08-09
      // at the owner's instruction ("worker message ของ sysadmin ให้ตัดออกครับ").
      // Replying to a worker is the owning Agent's job, and the Agent Portal
      // keeps its own SUPPORT > Messages entry pointing at the same screen. The
      // ROUTE stays registered in App.jsx, so an existing bookmark or an
      // audit-log link still resolves - only the sysadmin menu entry is gone.
      // Same nav-only removal as Fake Location Review and Footstep.
    ],
  },
  {
    // Renamed from "THAILAND JOBS" on 2026-08-10 at the owner's instruction
    // ("thailand job เปลี่ยนเป็น demand"), and moved directly under COMMAND in
    // the same breath ("แล้วย้ายขึ้นไปต่อจาก section command"). This is where
    // employer DEMAND is raised, which is daily operational work - so it sits
    // above the reference data rather than below it. Matching moved out into
    // its own section on 2026-08-11 (see below); what stays here is the demand
    // side only.
    title: "DEMAND",
    items: [
      // Jobs folded into Employers on 2026-08-05 at the owner's request
      // ("job ให้รวมเข้าที่ employer เลยครับ ไม่ต้องแยกอีกเมนูครับ แยกแล้วงง
      // ให้เพิ่ม job ได้ที่นายจ้างเลยครับ") - same treatment Worksites got a
      // day earlier, and for the same reason: a job only ever exists under an
      // employer, so two sibling menu items described one thing twice. The
      // /backoffice/thailand-jobs/jobs route stays registered in App.jsx and
      // is now an inner screen of Employers: the employer drawer's งาน tab
      // links into it (per job, and "งานทั้งหมดของนายจ้างรายนี้"), the
      // Dashboard job metrics still drill into it, and existing bookmarks
      // keep working. activeMatch keeps Employers highlighted while the
      // visitor is on either inner screen.
      {
        key: "employers",
        mark: "E",
        label: "Employers",
        to: "/backoffice/thailand-jobs/employers",
        activeMatch: ["/backoffice/thailand-jobs/jobs"],
      },
    ],
  },
  {
    // Split out of DEMAND on 2026-08-11 at the owner's instruction ("ให้แยก
    // matching ออกจาก demand เป็นอีก section นึงเลยครับ"). DEMAND is where an
    // employer's need is RAISED; matching is the separate act of pairing a
    // worker to it, and it carries its own inner screens (recommendation,
    // assignments) that had no visible home while they lived under one
    // Employers-shaped heading. The route and every activeMatch are unchanged,
    // so bookmarks, Dashboard drill-ins and audit-log links all still resolve -
    // this moves the menu entry only.
    title: "MATCHING",
    items: [
      {
        key: "matching-queue",
        mark: "MW",
        label: "Matching Demand กับแรงงาน",
        to: "/backoffice/thailand-jobs/matching-queue",
        activeMatch: [
          "/backoffice/thailand-jobs/matching-recommendation",
          "/backoffice/thailand-jobs/assignments",
        ],
      },
    ],
  },
  {
    /*
     * P162: the five-item WORKER INTAKE section is gone, replaced by the single
     * Workers screen the Agent Portal already uses - the owner's instruction was
     * "ที่ sysadmin ให้ตัด worker intake ออก และให้เอา worker profile แบบ agent
     * เข้ามาใส่แทน ให้เหมือนทุกอย่างแต่ให้ข้อมูล scope เป็น sysadmin".
     *
     * It is a better screen for the same job. The old section split one worker's
     * journey across four menu items (Invite/Applications -> e-KYC Review ->
     * Ready for Match -> Worker Profiles), so following a single person meant
     * walking the menu; the Workers screen is that journey as lifecycle tabs on
     * one page, with the real action for each stage on the row itself. Buddy
     * Safety was never built and sat there as a permanently-reserved item.
     *
     * Every one of those ROUTES stays registered in App.jsx, so bookmarks,
     * audit-log links and the Dashboard's own drill-downs keep working - the
     * same nav-only removal as Footstep, Fake Location Review and Worker
     * Messages.
     */
    title: "WORKERS",
    items: [{ key: "workers", mark: "W", label: "Workers", to: "/backoffice/workers" }],
  },
  {
    // Renamed from "WORKER LINE" on 2026-08-10 at the owner's instruction
    // ("ชื่อ worker line เปลี่ยนเป็น master data"): everything under it is the
    // reference data the rest of the product hangs off - the line itself, the
    // companies on both sides, and the Agents - not an operational queue.
    title: "MASTER DATA",
    items: [
      { key: "super-agents", mark: "SA", label: "Super Agents", to: "/backoffice/super-agents" },
      // P167's "จดหมายเชิญพันธมิตร" was folded into Super Agents on 2026-08-27 at
      // the owner's instruction ("แล้วเมนู โดนกลืนด้วยครับ") - the same
      // fold-into-parent move Worksites and Jobs got into Employers, and for the
      // same reason: a partner letter only ever exists under one Super Agent
      // line, so it is sent from that line's own row menu (จัดการ >
      // จดหมายเชิญพันธมิตร) rather than from a sibling menu item that describes
      // the same thing a second time. The ROUTE stays registered in App.jsx, so
      // the row-menu link, bookmarks and audit-log links all still resolve.
      // P161: the single "บริษัทแรงงาน / บริษัทจัดหางาน" entry mixed both sides
      // of the line on one screen, which is exactly the confusion the owner
      // asked to remove ("ให้แยก บริษัทจัดหางาน และ แรงงาน ออกจากกัน"). The two
      // sides recruit different subjects and their Agents are allowed to do
      // different things (P156), so each now has its own screen and its own
      // real-world name. The unfiltered /backoffice/worker-line/plcs route
      // stays registered and is still where a Super Agent's own "สร้าง บมจ/บจ
      // ใต้สายงานนี้" link lands, because that link already names its parent and
      // must not be filtered to a side the parent isn't on.
      { key: "plc-job-side", mark: "จง", label: "บริษัทจัดหางาน", to: "/backoffice/worker-line/plcs/job-side" },
      { key: "plc-worker-side", mark: "จร", label: "บริษัทจัดหาแรงงาน", to: "/backoffice/worker-line/plcs/worker-side" },
      { key: "agents", mark: "A", label: "Agents", to: "/backoffice/worker-line/agents" },
      { key: "worker-line-org-chart", mark: "O", label: "Worker Line Org Chart", to: "/backoffice/worker-line/org-chart" },
    ],
  },
  {
    title: "PLATFORM",
    items: [
      { key: "policies", mark: "PL", label: "Policies", reservedNote: "ยังไม่เปิดใช้งานหน้านี้" },
      { key: "users-roles", mark: "U", label: "Users / Roles", reservedNote: "ยังไม่เปิดใช้งานหน้านี้" },
      { key: "reports", mark: "RP", label: "Reports", reservedNote: "ยังไม่เปิดใช้งานหน้านี้" },
      { key: "audit-logs", mark: "AL", label: "Audit Logs", reservedNote: "ยังไม่เปิดใช้งานหน้านี้" },
      { key: "settings", mark: "ST", label: "Settings", reservedNote: "ยังไม่เปิดใช้งานหน้านี้" },
    ],
  },
];

/**
 * Agent Portal sidebar (Prompt 090), shown instead of NAV_SECTIONS when the
 * signed-in actor's primaryRole is "Agent" - matches the W43 reference
 * exactly. Invite Card was the only real, implemented screen through
 * Prompt 090.
 *
 * Prompt 138 flips Org Chart, Workers, SOS / Abnormal, Footstep, Zones, and
 * Contacts from reservedNote to real routes - every one of them reuses an
 * existing backend API (Worker Line Admin/Worker Profile/SOS/Abnormal/
 * Footstep/Geofence/Agent Contact endpoints all already shipped in earlier
 * packets) behind a brand new, Agent-scoped page under src/agent/ - never
 * the SuperAdmin page component directly, since those hardcode "SuperAdmin"
 * copy and expose cross-org filters meaningless to a single Agent. No new
 * backend route was added for any of these six.
 *
 * Prompt 138W-R2 adds "Applications" (/backoffice/agent/applications) - the
 * real, traceable Quick Registered application list every "Quick
 * Registered" count in the Agent Portal now links to (Dashboard funnel,
 * Org Chart, Invite Card's own metric tile). Reuses the same Prompt 093
 * Worker Application API WorkerApplicationInboxPage.jsx already calls for
 * SuperAdmin - no new backend endpoint.
 *
 * Audit Logs stays reservedNote - it has a confirmed backend gap:
 * IAuditLogRepository is write-only by design (its own doc comment says "a
 * future Audit Logs viewer packet can add read access") and no controller
 * anywhere exposes a GET for WmxAuditLogs. See AgentAuditLogsGapProof in
 * agentPortalRoutes.test.jsx for the grep-backed proof.
 *
 * Prompt 140A/140B flip "Buddy Guarantee" from reservedNote to a real route
 * (/backoffice/agent/buddy-guarantees) - Buddy Guarantee was never in
 * Prompt 138's scope (that packet's own comment above said so), but P140A
 * built the real backend (dbo.WmxBuddyGuarantees + a real CRUD API) and
 * P140B built this real screen over it, live-verified against the deployed
 * backend (no redeploy needed - P140A's own code was already live).
 *
 * Prompt 138R added "Dashboard" as the first real item, fixing a live bug:
 * the Agent sidebar had no Dashboard entry at all, and bare /backoffice was
 * rendering a hardcoded SuperAdmin dashboard for Agent sessions too. Prompt
 * 138X went further: BackofficeHomePage.jsx (bare /backoffice) is now the
 * single, role-aware dashboard for both roles, so this item's `to` points
 * straight at /backoffice - the old /backoffice/agent/dashboard route still
 * exists only as a redirect back to /backoffice for legacy deep links (see
 * App.jsx), so `activeMatch` still lists it to keep Dashboard highlighted
 * during the brief moment a visitor is still on that legacy URL.
 */
/**
 * P156: the Agent Portal menu for a JOB-side Agent (ฝั่งงาน).
 *
 * The owner's rule: "agent ที่สังกัดบริษัทจ้างงานจะส่ง invitecard ไม่ได้และมี
 * แรงงานไม่ได้ แต่จะมีสายงานคือนายจ้างแทนแรงงาน" - and then, on seeing it:
 * "safty ก็ไม่ต้องมีครับ เพราะไม่ใช่ฝั่งแรงงาน".
 *
 * That is exactly right, and it is why this is a separate list rather than
 * AGENT_NAV_SECTIONS with a few items hidden: almost every worker-side item is
 * meaningless here, not merely unavailable.
 *
 *   - Workers / Buddy Guarantee / Invite Card / Applications: this Agent holds
 *     no workers, so all four describe a pipeline it does not have.
 *   - The whole SAFETY section (War Room, SOS/Abnormal, Footstep, Zones) is
 *     worker TRACKING. No workers, nothing to track - so the section is gone
 *     rather than present and permanently empty.
 *
 * What replaces them is the employer side of the same product: Employers (and,
 * inside it, that employer's jobs). These point at the SAME
 * /backoffice/thailand-jobs/* routes
 * the SuperAdmin sidebar uses - the P154 Messages precedent - because those
 * pages already resolve visibility from the session server-side. Verified
 * against the live server signed in as this Agent: employers 2, jobs 1,
 * all his own. "ที่ admin ทำได้ สมชายก็ต้องทำได้แต่ในสังกัดของตัวเอง".
 *
 * Invite Card here is the EMPLOYER one: the Agent issues a link, the company
 * fills the form in itself at /employer-register, and the submission lands on
 * this Agent's Org Chart as pending. Same shape as the worker side, other
 * side's subject - which is exactly what the owner asked for.
 */
export const AGENT_JOB_SIDE_NAV_SECTIONS = [
  {
    title: "AGENT",
    items: [
      { key: "agent-dashboard", mark: "D", label: "Dashboard", to: "/backoffice", activeMatch: ["/backoffice/agent/dashboard"] },
      { key: "agent-org-chart", mark: "O", label: "Org Chart", to: "/backoffice/agent/org-chart" },
      { key: "agent-employer-invite", mark: "I", label: "Invite Card", to: "/backoffice/agent/employer-invite-cards" },
      // Jobs dropped here too on 2026-08-05 (owner), matching the SuperAdmin
      // sidebar above: a job is created and reached from inside its Employer,
      // so a second top-level entry only duplicated the same records. Same
      // rule as Worksites, dropped a day earlier for the same reason.
      {
        key: "agent-employers",
        mark: "E",
        label: "Employers",
        to: "/backoffice/thailand-jobs/employers",
        activeMatch: ["/backoffice/thailand-jobs/jobs"],
      },
    ],
  },
  {
    title: "SUPPORT",
    items: [
      { key: "agent-messages", mark: "MS", label: "Messages", to: "/backoffice/messages" },
      { key: "agent-contacts", mark: "C", label: "Contacts", to: "/backoffice/agent/contacts" },
      { key: "agent-audit-logs", mark: "G", label: "Audit Logs", reservedNote: "ยังไม่เปิดใช้งาน" },
    ],
  },
];

export const AGENT_NAV_SECTIONS = [
  {
    title: "AGENT",
    items: [
      { key: "agent-dashboard", mark: "D", label: "Dashboard", to: "/backoffice", activeMatch: ["/backoffice/agent/dashboard"] },
      { key: "agent-org-chart", mark: "O", label: "Org Chart", to: "/backoffice/agent/org-chart" },
      { key: "agent-invite-card", mark: "I", label: "Invite Card", to: "/backoffice/agent/invite-cards" },
      // P157: "Applications" was merged INTO Workers - a Quick Registered
      // applicant already appears in the Workers "รอ e-KYC" tab (Waiting state),
      // so two menu items described one population. The old page is gone and its
      // URL now redirects to Workers (App.jsx), so no activeMatch is needed.
      // Same fold-into-parent move as Worksites/Jobs -> Employers and Buddy
      // Guarantee -> Workers.
      { key: "agent-workers", mark: "W", label: "Workers", to: "/backoffice/agent/workers" },
      // P157: Buddy Guarantee is folded away from the sidebar. It confused
      // agents because the same thing is already done from the Workers screen
      // (/backoffice/agent/workers), so two entry points read as two features.
      // The route and its page stay (App.jsx), so deep links and the Workers
      // screen's own buddy actions keep working - only the duplicate top-level
      // menu item is gone. Same fold-into-parent move as Worksites/Jobs ->
      // Employers.
    ],
  },
  {
    title: "SAFETY",
    items: [
      { key: "agent-war-room", mark: "W", label: "War Room", to: "/backoffice/war-room" },
      { key: "agent-sos-abnormal", mark: "S", label: "SOS / Abnormal", to: "/backoffice/agent/sos-abnormal" },
      // P159: "Footstep" folded into War Room on 2026-08-08 at the owner's
      // instruction - a person's footstep is now read from the "ดู Footstep"
      // button at the top of the War Room person drawer, in the context of the
      // person you are investigating. The ROUTE stays registered in App.jsx, so
      // bookmarks, audit-log links and the drawer's own deep links all still
      // resolve; only the standalone menu entry is gone. Same fold-into-parent
      // move as Worksites/Jobs -> Employers.
      // P158: "Zones" removed from the AGENT menu at the owner's instruction -
      // a geofence zone is set by sysadmin only. The SuperAdmin menu keeps its
      // own "Geofence Zones" entry, and /backoffice/agent/zones stays routed so
      // an existing link or bookmark still resolves; it is simply no longer
      // something the Agent portal offers as a place to go.
    ],
  },
  {
    title: "SUPPORT",
    items: [
      // P154: the Agent's own half of the worker's messaging screen. Points
      // at the same /backoffice/messages route the SuperAdmin sidebar uses -
      // the page, the endpoints and the controls are identical for both
      // roles, and the Agent simply sees fewer conversations because
      // ChatService resolves scope from the session token. This is the
      // "agent replies from the backoffice" half of P154; the worker's half
      // is mobile-app/lib/features/agent_chat/.
      { key: "agent-messages", mark: "MS", label: "Messages", to: "/backoffice/messages" },
      { key: "agent-contacts", mark: "C", label: "Contacts", to: "/backoffice/agent/contacts" },
      { key: "agent-audit-logs", mark: "G", label: "Audit Logs", reservedNote: "ยังไม่เปิดใช้งาน" },
    ],
  },
];
